import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Turn an API error into a displayable string.
// FastAPI 422 responses put a list of {loc, msg, ...} objects in `detail`, which React cannot render.
export const getApiErrorMessage = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d?.loc ? `${d.loc.filter((p) => p !== 'body').join('.')}: ${d.msg}` : d?.msg || String(d)))
      .join('; ');
  }
  return error?.message || fallback;
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // FastAPI validation errors (422) send `detail` as a list of objects. Pages render
    // `error.response.data.detail` directly, and React crashes on objects - flatten it here once.
    if (error.response?.data && Array.isArray(error.response.data.detail)) {
      error.response.data.detail = getApiErrorMessage(error);
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  sendOTP: (phone) => api.post('/auth/send-otp', { phone }),
  verifyOTP: (phone, otp) => api.post('/auth/verify-otp', { phone, otp }),
  register: (data) => api.post('/auth/register', data),
  login: (identifier, password) => api.post('/auth/login', { identifier, password }),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (data) => api.put('/users/change-password', data),
  verifyGstin: (gstin) => api.get(`/users/verify-gstin/${gstin}`),

};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/products/', { params }), // Added trailing slash
  getOne: (id) => api.get(`/products/${id}`),
  getTrending: (days = 5, limit = 12) => api.get('/products/trending', { params: { days, limit } }),
  create: (data) => api.post('/admin/products', data),
  update: (id, data) => api.put(`/admin/products/${id}`, data),
  delete: (id) => api.delete(`/admin/products/${id}`),
  bulkUpload: (products) => api.post('/admin/products/bulk-upload', products),
  recoverMissingImages: () => api.post('/admin/products/recover-missing-images'),
  downloadCatalog: (wholesale = false) => api.get(`/admin/catalog/pdf${wholesale ? '?wholesale=true' : ''}`, { responseType: 'blob' }),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories/'), // Added trailing slash
  getTree: () => api.get('/categories/tree'),
  getFlat: () => api.get('/categories/flat'),
  getOne: (id) => api.get(`/categories/${id}`),
  getChildren: (id, recursive = false) => api.get(`/categories/${id}/children?recursive=${recursive}`),
  getPath: (id) => api.get(`/categories/${id}/path`),
  create: (data) => api.post('/admin/categories', data),
  update: (id, data) => api.put(`/admin/categories/${id}`, data),
  delete: (id, force = false) => api.delete(`/admin/categories/${id}?force=${force}`),
};

// Inventory API
export const inventoryAPI = {
  getAll: (params) => api.get('/admin/inventory', { params }),
  update: (productId, data) => api.put(`/admin/inventory/${productId}`, data),
};

// Orders API
export const ordersAPI = {
  create: (data) => api.post('/orders/', data),
  getUserOrders: () => api.get('/orders/'),
  getOne: (id) => api.get(`/orders/${id}`),
  getAll: (params) => api.get('/admin/orders', { params }),
  updateStatus: (id, data) => api.put(`/admin/orders/${id}/status/`, data),
  delete: (id) => api.delete(`/orders/${id}/`),
  getInvoice: (id) => api.get(`/orders/${id}/invoice`, { responseType: 'blob' }),
  getShippingLabel: (id) => api.get(`/orders/${id}/shipping-label`, { responseType: 'blob' }),
  getLabel: (id) => api.get(`/courier/label/${id}/`, { responseType: 'blob' }),
  // Cancellation endpoints
  checkCancellationEligibility: (id) => api.get(`/orders/${id}/can-cancel`),
  cancelOrder: (id, data) => api.post(`/orders/${id}/cancel`, data),
  // Return endpoints
  checkReturnEligibility: (id) => api.get(`/orders/${id}/can-return`),
  createReturn: (id, data) => api.post(`/orders/${id}/return`, data),
  getOrderReturns: (id) => api.get(`/orders/${id}/returns`),
  // New endpoints for returns management
  getReturns: (params) => api.get('/admin/returns', { params }),
  updateReturnStatus: (id, data) => api.put(`/admin/returns/${id}/status`, data),
  recalculateStatuses: () => api.post('/admin/orders/recalculate-statuses'),
};

// POS API
export const posAPI = {
  createSale: (data) => api.post('/admin/pos/sale', data),
  createWholesaleSale: (data) => api.post('/orders/wholesale-sale', data), // New wholesale sale endpoint
  updateSale: (id, data) => api.put(`/admin/pos/sale/${id}`, data),
  createQuotation: (data) => api.post('/admin/pos/quotation', data),
  createWholesaleQuotation: (data) => api.post('/orders/quotation', data), // Wholesale quotation endpoint
  returnItems: (data) => api.post('/admin/pos/return', data),
  searchCustomer: (query) => api.get('/admin/pos/search-customer', { params: { query } }),
};

// Returns API
export const returnsAPI = {
  create: (data) => api.post('/returns', data),
  getAll: (params) => api.get('/admin/returns', { params }),
  getUserReturns: () => api.get('/returns'),
  update: (id, data) => api.put(`/admin/returns/${id}`, data),
  getById: (id) => api.get(`/returns/${id}`),
  uploadEvidence: (id, formData) => api.post(`/returns/${id}/evidence`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  trackReturn: (id) => api.get(`/returns/${id}/tracking`),
};

// Reviews API
export const reviewsAPI = {
  create: (data) => api.post('/reviews/', data),
  getAll: (productId) => api.get('/reviews/', { params: { product_id: productId } }),
  delete: (id) => api.delete(`/reviews/${id}`),
};

// Banners API
export const bannersAPI = {
  getAll: () => api.get('/banners/'), // Added trailing slash
  create: (data) => api.post('/admin/banners', data),
  update: (id, data) => api.put(`/admin/banners/${id}`, data),
  delete: (id) => api.delete(`/admin/banners/${id}`),
};

// Offers API
export const offersAPI = {
  getAll: () => api.get('/offers/'), // Added trailing slash
  create: (data) => api.post('/admin/offers', data),
  update: (id, data) => api.put(`/admin/offers/${id}`, data),
  delete: (id) => api.delete(`/admin/offers/${id}`),
};

// Couriers API
export const couriersAPI = {
  getAll: () => api.get('/admin/couriers'),
  create: (data) => api.post('/admin/couriers', data),
  update: (id, data) => api.put(`/admin/couriers/${id}`, data),
  delete: (id) => api.delete(`/admin/couriers/${id}`),
  // New shipping integration endpoints
  createShipment: (data) => api.post('/courier/create-shipment', data),
  scheduleReturnPickup: (data) => api.post('/courier/schedule-return-pickup', data),
  trackShipment: (awb) => api.get(`/courier/track/${awb}`),
  cancelShipment: (awb) => api.post(`/courier/cancel/${awb}`),
};

// Payment Gateways API
export const paymentGatewaysAPI = {
  getAll: () => api.get('/admin/payment-gateways'),
  create: (data) => api.post('/admin/payment-gateways', data),
  update: (id, data) => api.put(`/admin/payment-gateways/${id}`, data),
  delete: (id) => api.delete(`/admin/payment-gateways/${id}`),
  verify: (data) => api.post('/payments/verify', data),
  getPublic: () => api.get('/payments/public'),
  initiate: (data) => api.post('/payments/initiate', data),
  confirmMock: (data) => api.post('/payments/confirm-mock', data),
  verifyRazorpay: (data) => api.post('/payments/verify-razorpay', data),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/admin/settings/'),
  update: (data) => api.put('/admin/settings/', data),
  getPublic: () => api.get('/settings/public'),
  getEmailSettings: () => api.get('/admin/settings/email'),
  testEmail: (data) => api.post('/admin/settings/email/test', data),
  getSmsSettings: () => api.get('/admin/settings/sms'),
  updateSmsSettings: (data) => api.put('/admin/settings/sms', data),
  updateEmailSettings: (data) => api.put('/admin/settings/email', data),
};

// Reports API
export const reportsAPI = {
  getSales: (params) => api.get('/admin/reports/sales', { params }),
  getInventory: () => api.get('/admin/reports/inventory'),
  getInventoryStatus: () => api.get('/admin/reports/inventory-status'),
  getProfitLoss: (params) => api.get('/admin/reports/profit-loss', { params }),
};

// Courier API
export const courierAPI = {
  checkPincode: (pincode) => api.get('/courier/pincode', { params: { pincode } }),
  validateAddress: (addressData) => api.post('/courier/validate-address', addressData),
  shipOrder: (orderId) => api.post(`/courier/ship/${orderId}`),
  trackOrder: (orderId) => api.get(`/courier/track/${orderId}`),
  trackByAwb: (awb) => api.get(`/courier/track-by-awb/${awb}`),
  getLabel: (orderId) => api.get(`/courier/label/${orderId}`),
  getInvoice: (orderId) => api.get(`/courier/invoice/${orderId}`),
  cancelShipment: (orderId) => api.post(`/courier/cancel/${orderId}`),
  createReturn: (orderId, returnData) => api.post(`/admin/couriers/create-return/${orderId}`, returnData),
  bulkTrackUpdate: () => api.post('/courier/bulk-track-update'),
  getPicklist: (date) => api.get('/admin/picklist', { params: { date } }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/admin/dashboard'),
  seedData: () => api.post('/admin/seed-data'),
  clearData: () => api.post('/admin/reset-data'),
};

// Pages API
export const pagesAPI = {
  getPrivacyPolicy: () => api.get('/pages/privacy-policy'),
  getTerms: () => api.get('/pages/terms'),
  getReturnPolicy: () => api.get('/pages/return-policy'),
  getShippingPolicy: () => api.get('/pages/shipping-policy'),
  getShippingInfo: () => api.get('/pages/shipping-info'),
  getContact: () => api.get('/pages/contact'),
  submitContact: (data) => api.post('/contact', data),
  updatePage: (slug, data) => api.put(`/pages/${slug}`, data),
};

// Notifications API
export const notificationsAPI = {
  getUser: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete('/notifications'),
  getAdmin: () => api.get('/admin/notifications'),
  markAdminRead: (id) => api.put(`/admin/notifications/${id}/read`),
};

// GST Verification API
export const gstAPI = {
  getCaptcha: () => api.get('/users/gst/captcha'),
  verifyGST: (data) => api.post('/users/gst/verify', data),
};

// Seller Requests API
export const sellerRequestsAPI = {
  request: (data) => api.post('/users/request-seller-access', data),
  getAll: (params) => api.get('/admin/seller-requests', { params }),
  update: (id, data) => api.put(`/admin/seller-requests/${id}`, data),
};

// POS Requests API
export const posRequestsAPI = {
  request: (data) => api.post('/users/request-pos-access', data),
  getAll: (params) => api.get('/admin/pos-requests', { params }),
  update: (id, data) => api.put(`/admin/pos-requests/${id}`, data),
  switchToCustomer: () => api.post('/users/switch-to-customer'),
};

// Team API
export const teamAPI = {
  getAll: () => api.get('/admin/team'),
  create: (data) => api.post('/admin/team', data),
  delete: (id) => api.delete(`/admin/team/${id}`),
};

// Customers API
export const customersAPI = {
  getAll: (params) => api.get('/admin/customers', { params }),
  getOne: (id) => api.get(`/admin/customers/${id}`),
  update: (id, data) => api.put(`/admin/customers/${id}`, data),
};

// Pincode API
export const pincodeAPI = {
  verify: (pincode) => api.post('/verify-pincode', { pincode }),
};

// Packing Slip & Labels API
export const printAPI = {
  getPackingSlip: (orderId) => api.get(`/orders/${orderId}/packing-slip`),
  getLabel: (orderId) => api.get(`/orders/${orderId}/label`),
  getBulkLabels: (date) => api.get('/admin/orders/bulk-labels', { params: { date } }),
};

// Payment QR API
export const paymentAPI = {
  getQR: (amount, customerName = 'Customer', orderNumber = '') =>
    api.post('/generate-qr', {
      amount,
      customer_name: customerName,
      order_number: orderNumber
    }),
};

// Product Lookup API
export const productLookupAPI = {
  bySku: (sku) => api.get('/products/lookup', { params: { sku } }),
  byBarcode: (barcode) => api.get('/products/lookup', { params: { barcode } }),
};

// Upload API
export const uploadAPI = {
  uploadImage: (file, folder = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    return api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadMultiple: (files, folder = 'general') => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('folder', folder);
    return api.post('/upload/multiple', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteImage: (fileUrl) => api.delete('/upload/delete', { params: { file_url: fileUrl } }),
};

export default api;
